import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_CHATBOT,
  CHATBOT_SEND_MESSAGE,
} from '../../../Constants/Config';
import {
  clearChatbotConversation,
  loadChatbotConversation,
  normalizeChatbotEmailId,
  normalizeChatbotMerchantId,
  saveChatbotConversation,
} from '../../../utils/chatbotConversationStorage';
import { invalidateChatbotEncryptionKeyCache } from '../../../utils/chatbotConversationCrypto';

const getChatbotHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const apiKey = process.env.REACT_APP_CHATBOT_API_KEY;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }
  return headers;
};

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join(', ') || fallback;
  }
  return detail || error?.response?.data?.message || error?.message || fallback;
};

const persistConversationSnapshot = async ({
  merchantCode,
  emailId,
  messages,
  activeThreadId,
}) => {
  const merchantId = normalizeChatbotMerchantId(merchantCode);
  const email = normalizeChatbotEmailId(emailId);
  if (!merchantId) return false;
  return saveChatbotConversation(merchantId, email, {
    messages: Array.isArray(messages) ? messages : [],
    threadId: activeThreadId,
  });
};

/**
 * Decrypt + hydrate only for the live merchant_id + email_id scope.
 * Ignores stale results if the user/merchant changed mid-flight.
 */
export const hydrateChatbotConversation = createAsyncThunk(
  'chatbot/hydrateConversation',
  async ({ merchantId, emailId }, { getState, rejectWithValue }) => {
    const merchant = normalizeChatbotMerchantId(merchantId);
    const email = normalizeChatbotEmailId(emailId);
    if (!merchant) {
      return rejectWithValue('Merchant is required.');
    }

    try {
      const saved = await loadChatbotConversation(merchant, email);
      const current = getState()?.chatbot;
      if (
        normalizeChatbotMerchantId(current?.merchantCode) !== merchant ||
        normalizeChatbotEmailId(current?.emailId) !== email
      ) {
        return { merchantId: merchant, emailId: email, skipped: true };
      }

      return {
        merchantId: merchant,
        emailId: email,
        skipped: false,
        threadId: saved?.threadId || null,
        messages: Array.isArray(saved?.messages) ? saved.messages : [],
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Unable to load conversation.');
    }
  },
);

export const sendChatbotMessage = createAsyncThunk(
  'chatbot/sendMessage',
  async (
    {
      message,
      merchantId,
      emailId = '',
      threadId = null,
      orderMethod = 'pickup',
      chatHistory = [],
    },
    { rejectWithValue },
  ) => {
    try {
      const normalizedOrderMethod =
        orderMethod === 'delivery' || orderMethod === 'pickup'
          ? orderMethod
          : 'pickup';
      const normalizedMerchantId = normalizeChatbotMerchantId(merchantId);
      const normalizedEmailId = normalizeChatbotEmailId(
        emailId == null ? '' : emailId,
      );

      if (!normalizedMerchantId) {
        return rejectWithValue('Merchant is required.');
      }

      const response = await axios.post(
        `${BASE_URL_CHATBOT}/${CHATBOT_SEND_MESSAGE}`,
        {
          message: message.trim(),
          merchant_id: normalizedMerchantId,
          email_id: normalizedEmailId,
          merchant_code: normalizedMerchantId,
          order_method: normalizedOrderMethod,
          thread_id: threadId || null,
          chat_history: Array.isArray(chatHistory) ? chatHistory : [],
        },
        { headers: getChatbotHeaders() },
      );
      if (response.data?.success) {
        return response.data;
      }
      return rejectWithValue('Unable to send message.');
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Unable to send message.'));
    }
  },
);

/**
 * Encrypt-persist current conversation, then keep panel closed.
 * isOpen clears in pending so route changes cannot remount an open panel.
 */
export const minimizeChatbot = createAsyncThunk(
  'chatbot/minimize',
  async (_, { getState }) => {
    const state = getState().chatbot;
    await persistConversationSnapshot({
      merchantCode: state.merchantCode,
      emailId: state.emailId,
      messages: state.messages,
      activeThreadId: state.activeThreadId,
    });
    return true;
  },
);

const initialState = {
  isOpen: false,
  merchantCode: null,
  emailId: '',
  activeThreadId: null,
  messages: [],
  sending: false,
  hydrating: false,
  error: null,
};

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    setChatbotContext: (state, { payload }) => {
      const nextMerchant = normalizeChatbotMerchantId(payload?.merchantCode);
      const nextEmail = normalizeChatbotEmailId(payload?.emailId);
      const merchantChanged = state.merchantCode !== nextMerchant;
      const emailChanged = state.emailId !== nextEmail;

      if (!merchantChanged && !emailChanged) {
        if (nextMerchant) state.merchantCode = nextMerchant;
        state.emailId = nextEmail;
        // Same scope with no in-memory messages — allow decrypt restore.
        if (nextMerchant && state.messages.length === 0) {
          state.hydrating = true;
        }
        return;
      }

      // Snapshot BEFORE clearing so encrypt-persist still has the prior user's data.
      if (state.merchantCode) {
        void persistConversationSnapshot({
          merchantCode: state.merchantCode,
          emailId: state.emailId,
          messages: state.messages,
          activeThreadId: state.activeThreadId,
        });
      }

      // Drop decrypted messages immediately — never show another user/merchant chat.
      state.merchantCode = nextMerchant || null;
      state.emailId = nextEmail;
      state.activeThreadId = null;
      state.messages = [];
      state.error = null;
      state.hydrating = Boolean(nextMerchant);
    },
    openChatbot: (state, { payload }) => {
      const merchant =
        normalizeChatbotMerchantId(payload?.merchantCode) || state.merchantCode;
      const email =
        payload?.emailId !== undefined
          ? normalizeChatbotEmailId(payload.emailId)
          : state.emailId;

      const merchantChanged = state.merchantCode !== merchant;
      const emailChanged = state.emailId !== email;

      if (merchantChanged || emailChanged) {
        if (state.merchantCode) {
          void persistConversationSnapshot({
            merchantCode: state.merchantCode,
            emailId: state.emailId,
            messages: state.messages,
            activeThreadId: state.activeThreadId,
          });
        }
        state.merchantCode = merchant || null;
        state.emailId = email;
        state.activeThreadId = null;
        state.messages = [];
        state.hydrating = Boolean(merchant);
      } else if (merchant && state.messages.length === 0) {
        state.hydrating = true;
      }

      state.isOpen = true;
      state.error = null;
    },
    closeAndClearChatbot: (state) => {
      const merchant = normalizeChatbotMerchantId(state.merchantCode);
      const email = normalizeChatbotEmailId(state.emailId);
      if (merchant) {
        clearChatbotConversation(merchant, email);
      }
      state.isOpen = false;
      state.activeThreadId = null;
      state.messages = [];
      state.error = null;
      state.sending = false;
      state.hydrating = false;
    },
    clearChatbotError: (state) => {
      state.error = null;
    },
    resetChatbot: () => {
      invalidateChatbotEncryptionKeyCache();
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateChatbotConversation.pending, (state, { meta }) => {
        const merchant = normalizeChatbotMerchantId(meta?.arg?.merchantId);
        const email = normalizeChatbotEmailId(meta?.arg?.emailId);
        if (
          normalizeChatbotMerchantId(state.merchantCode) === merchant &&
          normalizeChatbotEmailId(state.emailId) === email &&
          (state.hydrating || state.messages.length === 0)
        ) {
          state.hydrating = true;
        }
      })
      .addCase(hydrateChatbotConversation.fulfilled, (state, { payload }) => {
        if (!payload || payload.skipped) {
          state.hydrating = false;
          return;
        }
        if (
          normalizeChatbotMerchantId(state.merchantCode) !==
            payload.merchantId ||
          normalizeChatbotEmailId(state.emailId) !== payload.emailId
        ) {
          return;
        }
        // Keep newer in-memory messages if we weren't waiting on a hydrate
        // (e.g. remount while minimize encrypt-persist is still in flight).
        if (!state.hydrating && state.messages.length > 0) {
          return;
        }
        state.activeThreadId = payload.threadId || null;
        state.messages = Array.isArray(payload.messages) ? payload.messages : [];
        state.hydrating = false;
      })
      .addCase(hydrateChatbotConversation.rejected, (state) => {
        state.hydrating = false;
      })
      .addCase(minimizeChatbot.pending, (state) => {
        state.isOpen = false;
        state.error = null;
      })
      .addCase(minimizeChatbot.rejected, (state) => {
        state.isOpen = false;
      })
      .addCase(sendChatbotMessage.pending, (state, { meta }) => {
        state.sending = true;
        state.error = null;
        const userMessage = meta?.arg?.message?.trim();
        if (userMessage) {
          state.messages.push({
            id: `pending-${Date.now()}`,
            role: 'user',
            content: userMessage,
            pending: true,
          });
        }
      })
      .addCase(sendChatbotMessage.fulfilled, (state, { payload, meta }) => {
        state.sending = false;
        if (meta?.arg?.merchantId) {
          state.merchantCode = normalizeChatbotMerchantId(meta.arg.merchantId);
        }
        if (meta?.arg?.emailId !== undefined) {
          state.emailId = normalizeChatbotEmailId(meta.arg.emailId);
        }
        state.activeThreadId = payload?.thread_id || state.activeThreadId;
        state.messages = state.messages.filter((msg) => !msg.pending);
        const sentMessage = meta?.arg?.message?.trim();
        if (sentMessage) {
          state.messages.push({
            id: `user-${Date.now()}`,
            role: 'user',
            content: sentMessage,
          });
        }
        if (payload?.message) {
          state.messages.push({
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: payload.message,
          });
        }
        void persistConversationSnapshot({
          merchantCode: state.merchantCode,
          emailId: state.emailId,
          messages: state.messages,
          activeThreadId: state.activeThreadId,
        });
      })
      .addCase(sendChatbotMessage.rejected, (state, { payload }) => {
        state.sending = false;
        state.messages = state.messages.filter((msg) => !msg.pending);
        state.error = payload;
      });
  },
});

export const {
  setChatbotContext,
  openChatbot,
  closeAndClearChatbot,
  clearChatbotError,
  resetChatbot,
} = chatbotSlice.actions;

export default chatbotSlice.reducer;
